/**
 * Utilities for encoding/decoding chart data in URLs
 */

import pako from 'pako';

import type { ChartData } from '@/types/chart';

import { validateChartData } from './dataValidation';

/**
 * Encode chart data to URL-safe compressed string
 */
export function encodeChartData(chartData: ChartData): string {
  const jsonString = JSON.stringify(chartData);

  // Compress with gzip
  const compressed = pako.gzip(jsonString);

  // Base64 encode
  const base64 = btoa(String.fromCharCode(...new Uint8Array(compressed)));

  // Make URL-safe: replace +/= with -_~
  const urlSafe = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '~');

  return urlSafe;
}

/**
 * Decode chart data from URL-safe compressed string
 */
export function decodeChartData(encoded: string): ChartData | null {
  try {
    // Revert URL-safe encoding
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/').replace(/~/g, '=');

    // Base64 decode
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decompress
    const decompressed = pako.ungzip(bytes, { to: 'string' });

    // Parse JSON
    const parsed = JSON.parse(decompressed);

    // Validate against schema
    const result = validateChartData(parsed);
    if (!result.success || !result.data) {
      return null;
    }

    return result.data;
  } catch {
    return null;
  }
}
